// StaffProgressDetail.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StaffLayout from "../components/StaffLayout";

export {}; // module safety

type MerchantProgressDetail = {
  ok: boolean;
  merchant: {
    merchant_id: string;
    company_name: string;
    industry?: string;
    merchant_type?: string; // 法人 / 個人
    updated_at: string;
  };
  progress: {
    merchant_id: string;
    docs_total: number;
    docs_completed: number;
    uploads_total: number;
    uploads_completed: number;
    status: string;
    forms: Record<string, boolean>;
  };
};

type StaffDocumentItem = {
  document_key: string;
  document_label: string;
  other_title: string;
  file_name: string;
  file_path: string;
  uploaded_at: string;
};

type ChecklistResponse = {
  ok: boolean;
  merchant_id: string;
  required: {
    forms: string[];
    docs: string[];
  };
  checked: {
    form: Record<string, boolean>;
    doc: Record<string, boolean>;
  };
  all_checked: boolean;
};

const BASE_URL = "/api";

const getStaffAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem("staff_google_token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};



function CircleProgress({ label, value, total }: { label: string; value: number; total: number }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const r = 42;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;

  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        padding: 14,
        background: "#f8fafc",
        display: "flex",
        gap: 14,
        alignItems: "center",
      }}
    >
      <svg width="110" height="110" viewBox="0 0 110 110">
        <circle cx="55" cy="55" r={r} strokeWidth="10" stroke="#e2e8f0" fill="none" />
        <circle
          cx="55"
          cy="55"
          r={r}
          strokeWidth="10"
          stroke="#16a34a"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 55 55)"
        />
        <text x="55" y="58" textAnchor="middle" fontSize="18" fontWeight="900" fill="#16a34a">
          {pct}%
        </text>
      </svg>

      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 900, color: "#0f172a" }}>{label}</div>
        <div style={{ marginTop: 6, fontWeight: 900, fontSize: 18, color: "#16a34a" }}>
          {value} / {total}
        </div>
        <div style={{ marginTop: 6, color: "#64748b", fontWeight: 700, fontSize: 12 }}>
          進捗率（自動計算）
        </div>
      </div>
    </div>
  );
}

export default function StaffProgressDetail() {
  const navigate = useNavigate();
  const { merchantId } = useParams();

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<MerchantProgressDetail | null>(null);
  const [error, setError] = useState("");

  // ✅ Section toggle: "forms" or "uploads"
  const [section, setSection] = useState<"forms" | "uploads">("forms");

  // ✅ Uploaded documents (staff view)
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState("");
  const [docs, setDocs] = useState<StaffDocumentItem[]>([]);

  // ✅ Checklist
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistError, setChecklistError] = useState("");
  const [checklist, setChecklist] = useState<ChecklistResponse | null>(null);

  const formsList = useMemo(
    () => [
      { key: "form1_company_overview", label: "フォーム① 会社概要" },
      { key: "form2_representative", label: "フォーム② 代表者情報" },
      { key: "form3_payment_handling", label: "フォーム③ 決済取扱" },
      { key: "form4_products", label: "フォーム④ 取扱商品" },
      { key: "form5_sales_method", label: "フォーム⑤ 販売方法" },
      { key: "form6_store_list", label: "フォーム⑥ 店舗一覧" },
      { key: "form7_contact_person", label: "フォーム⑦ ご担当者情報" },
    ],
    []
  );

 

  // --------------------------
  // Fetch: progress detail
  // --------------------------
  useEffect(() => {
    const run = async () => {
      if (!merchantId) return;

      setError("");
      setLoading(true);

      try {
        const res = await fetch(`${BASE_URL}/staff/progress/${merchantId}`, {
          headers: getStaffAuthHeaders(),
        });

        const json = await res.json();
        if (!res.ok) throw new Error(json?.detail || "detail fetch failed");

        setDetail(json);
        setLoading(false);
      } catch (e: any) {
        setError(e?.message || "加盟店の進捗詳細の取得に失敗しました。");
        setLoading(false);
      }
    };

    run();
  }, [merchantId]);

  // --------------------------
  // Fetch: checklist (always)
  // --------------------------
  useEffect(() => {
    const runChecklist = async () => {
      if (!merchantId) return;

      setChecklistError("");
      setChecklistLoading(true);

      try {
        const res = await fetch(`${BASE_URL}/staff/checklist/${merchantId}`, {
  headers: {
    ...getStaffAuthHeaders(),
  },
});
        const json = await res.json();
        if (!res.ok) throw new Error(json?.detail || "checklist fetch failed");

        setChecklist(json as ChecklistResponse);
        setChecklistLoading(false);
      } catch (e: any) {
        setChecklistError(e?.message || "チェックリストの取得に失敗しました。");
        setChecklistLoading(false);
      }
    };

    runChecklist();
  }, [merchantId]);

  // --------------------------
  // Fetch: uploaded docs (only when uploads tab)
  // --------------------------
  useEffect(() => {
    const runDocs = async () => {
      if (!merchantId) return;
      if (section !== "uploads") return;

      setDocsError("");
      setDocsLoading(true);

      try {
        const res = await fetch(`${BASE_URL}/staff/documents/${merchantId}`, {
  headers: {
    ...getStaffAuthHeaders(),
  },
});
        const json = await res.json();
        if (!res.ok) throw new Error(json?.detail || "documents fetch failed");

        const items: StaffDocumentItem[] = Array.isArray(json?.items) ? json.items : [];
        setDocs(items);
        setDocsLoading(false);
      } catch (e: any) {
        setDocsError(e?.message || "書類一覧の取得に失敗しました。");
        setDocsLoading(false);
      }
    };

    runDocs();
  }, [merchantId, section]);

  // --------------------------
  // Helpers
  // --------------------------
  const refreshChecklist = async () => {
    if (!merchantId) return;
    try {
      const res = await fetch(`${BASE_URL}/staff/checklist/${merchantId}`, {
  headers: {
    ...getStaffAuthHeaders(),
  },
});
      const json = await res.json();
      if (res.ok) setChecklist(json as ChecklistResponse);
    } catch {
      // ignore
    }
  };

  const setChecklistItem = async (item_type: "form" | "doc", item_key: string, is_checked: boolean) => {
    if (!merchantId) return;

    // optimistic update
    setChecklist((prev) => {
      if (!prev) return prev;
      const next: ChecklistResponse = {
        ...prev,
        checked: {
          ...prev.checked,
          [item_type]: {
            ...(prev.checked?.[item_type] || {}),
            [item_key]: is_checked,
          },
        },
        all_checked: prev.all_checked, // will be corrected after refresh
      };
      return next;
    });

    try {
      const res = await fetch(`${BASE_URL}/staff/checklist/${merchantId}`, {
  method: "PUT",
  headers: {
    "Content-Type": "application/json",
    ...getStaffAuthHeaders(),
  },
  body: JSON.stringify({ item_type, item_key, is_checked }),
});

      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "checklist update failed");

      // backend returns the newest checked + all_checked
      setChecklist((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          checked: json.checked || prev.checked,
          all_checked: !!json.all_checked,
        };
      });
    } catch (e) {
      // if update fails, pull from server
      await refreshChecklist();
    }
  };

  const requiredForms = checklist?.required?.forms || [];
  const requiredDocs = checklist?.required?.docs || [];
  const checkedForms = checklist?.checked?.form || {};
  const checkedDocs = checklist?.checked?.doc || {};
  const allChecked = !!checklist?.all_checked;

  const uploadedMap = useMemo(() => {
    const m: Record<string, StaffDocumentItem> = {};
    for (const d of docs) {
      if (d?.document_key) m[d.document_key] = d;
    }
    return m;
  }, [docs]);

  return (
    <StaffLayout title="進捗管理（詳細）" active="progress">
      <button
        onClick={() => navigate("/staff/progress")}
        style={{
          padding: "10px 14px",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          background: "#fff",
          color: "#0f172a",
          fontWeight: 900,
          cursor: "pointer",
          marginBottom: 12,
        }}
      >
        一覧へ戻る
      </button>

      {loading && <div style={{ color: "#475569", fontWeight: 800 }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginTop: 10,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && detail && (
        <>
          {/* Merchant info */}
          <div
            style={{
              border: "1px solid #bae6fd",
              background: "#f0f9ff",
              borderRadius: 14,
              padding: 14,
              marginBottom: 14,
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 6 }}>加盟店情報</div>

            <div style={{ lineHeight: 1.8, fontWeight: 500, color: "#0f172a" }}>
              <div>加盟店ID：{detail.merchant.merchant_id}</div>
              <div>会社名：{detail.merchant.company_name}</div>
              <div>業種：{detail.merchant.industry || "-"}</div>
              <div>区分：{detail.merchant.merchant_type || "-"}</div>
              <div>ステータス：{detail.progress.status}</div>
              <div style={{ color: "#475569", fontWeight: 500, fontSize: 13 }}>
                最終更新：{detail.merchant.updated_at}
              </div>
            </div>
          </div>

          {/* Circles */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <CircleProgress
              label="フォーム入力（7件）"
              value={detail.progress.docs_completed}
              total={detail.progress.docs_total}
            />
            <CircleProgress
              label={`書類アップロード（${detail.progress.uploads_total}件）`}
              value={detail.progress.uploads_completed}
              total={detail.progress.uploads_total}
            />
          </div>

          {/* Checklist load errors (non-blocking) */}
          {checklistLoading && (
            <div style={{ marginTop: 10, color: "#475569", fontWeight: 800 }}>チェックリスト読み込み中...</div>
          )}
          {checklistError && (
            <div
              style={{
                marginTop: 10,
                background: "#fff1f2",
                border: "1px solid #fecdd3",
                color: "#b91c1c",
                padding: "10px 12px",
                borderRadius: 12,
                fontWeight: 800,
              }}
            >
              {checklistError}
            </div>
          )}

          {/* Section toggle boxes */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
            <div
              onClick={() => setSection("forms")}
              style={{
                padding: 14,
                borderRadius: 14,
                border: section === "forms" ? "2px solid #2563eb" : "1px solid #2563eb",
                background: section === "forms" ? "#eef2ff" : "#fff",
                cursor: "pointer",
                fontWeight: 900,
                color: "#0f172a",
              }}
            >
              フォーム一覧
            </div>

            <div
              onClick={() => setSection("uploads")}
              style={{
                padding: 14,
                borderRadius: 14,
                border: section === "uploads" ? "2px solid #2563eb" : "1px solid #2563eb",
                background: section === "uploads" ? "#eef2ff" : "#fff",
                cursor: "pointer",
                fontWeight: 900,
                color: "#0f172a",
              }}
            >
              アップロード書類
            </div>
          </div>

          {/* -------------------------
             Forms section
          ------------------------- */}
          {section === "forms" ? (
            <div
              style={{
                marginTop: 14,
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                background: "#fff",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: 12,
                  fontWeight: 900,
                  background: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                フォーム別 完了状況
              </div>

              <div style={{ padding: 12 }}>
                {formsList.map((f) => {
                  const done = !!detail.progress.forms?.[f.key];
                  const isRequired = requiredForms.includes(f.key) || requiredForms.length === 0;
                  const checked = !!checkedForms[f.key];

                  const handleClick = () => {
                    if (!merchantId) return;
                    if (!done) return;
                    navigate(`/staff/progress/${merchantId}/forms/${f.key}`);
                  };

                  const canCheck = done && isRequired;

                  return (
                    <div
                      key={f.key}
                      onClick={handleClick}
                      role={done ? "button" : undefined}
                      tabIndex={done ? 0 : -1}
                      onKeyDown={(e) => {
                        if (!done) return;
                        if (e.key === "Enter" || e.key === " ") handleClick();
                      }}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "10px 10px",
                        borderRadius: 10,
                        border: done ? "1px solid #bbf7d0" : "1px solid #f1f5f9",
                        marginBottom: 8,
                        background: done ? "#ecfdf5" : "#fff7ed",
                        cursor: done ? "pointer" : "not-allowed",
                        opacity: done ? 1 : 0.85,
                        userSelect: "none",
                        alignItems: "center",
                        gap: 10,
                      }}
                      title={done ? "クリックして入力内容を表示" : "未完了のため表示できません"}
                    >
                      <div
                        style={{
                          fontWeight: 900,
                          color: "#0f172a",
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          minWidth: 0,
                        }}
                      >
                        <span>{f.label}</span>
                        {done && (
                          <span style={{ fontSize: 12, fontWeight: 900, color: "#0b1f3b" }}>
                            （詳細を見る）
                          </span>
                        )}
                      </div>

                      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontWeight: 900,
                            cursor: canCheck ? "pointer" : "not-allowed",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!canCheck}
                            onChange={(e) => {
                              e.stopPropagation();
                              setChecklistItem("form", f.key, e.target.checked);
                            }}
                            style={{
                              width: 18,
                              height: 18,
                              cursor: canCheck ? "pointer" : "not-allowed",
                            }}
                          />
                        </label>

                        <div style={{ fontWeight: 900, color: done ? "#166534" : "#9a3412", minWidth: 46 }}>
                          {done ? "完了" : "未完了"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* -------------------------
               Uploads section (Required docs checklist style)
            ------------------------- */
            <div
              style={{
                marginTop: 14,
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                background: "#fff",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: 12,
                  fontWeight: 900,
                  background: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                アップロード書類 一覧（必須書類チェック）
              </div>

              <div style={{ padding: 12 }}>
                {docsLoading && <div style={{ color: "#475569", fontWeight: 800 }}>読み込み中...</div>}

                {docsError && (
                  <div
                    style={{
                      marginTop: 10,
                      background: "#fff1f2",
                      border: "1px solid #fecdd3",
                      color: "#b91c1c",
                      padding: "10px 12px",
                      borderRadius: 12,
                      fontWeight: 800,
                    }}
                  >
                    {docsError}
                  </div>
                )}

                {!docsLoading && !docsError && requiredDocs.length === 0 && (
                  <div style={{ color: "#475569", fontWeight: 800 }}>
                    必須書類はありません（区分/業種が未選択の可能性があります）。
                  </div>
                )}

                {!docsLoading &&
                  !docsError &&
                  requiredDocs.map((docKey) => {
                    const uploaded = uploadedMap[docKey];
                    const checked = !!checkedDocs[docKey];
                    const done = !!uploaded;
                    const canCheck = done; // only check if uploaded exists

                    const title = uploaded?.document_label
                      ? uploaded.other_title
                        ? `${uploaded.document_label}（${uploaded.other_title}）`
                        : uploaded.document_label
                      : docKey;

                    const handleClick = () => {
                      if (!merchantId) return;
                      if (!done) return;
                      navigate(`/staff/progress/${merchantId}/documents/${docKey}`);
                    };

                    return (
                      <div
                        key={docKey}
                        style={{
                          borderRadius: 10,
                          border: done ? "1px solid #bbf7d0" : "1px solid #f1f5f9",
                          marginBottom: 10,
                          background: done ? "#ecfdf5" : "#fff7ed",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          onClick={handleClick}
                          role={done ? "button" : undefined}
                          tabIndex={done ? 0 : -1}
                          onKeyDown={(e) => {
                            if (!done) return;
                            if (e.key === "Enter" || e.key === " ") handleClick();
                          }}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "10px 10px",
                            cursor: done ? "pointer" : "not-allowed",
                            opacity: done ? 1 : 0.85,
                            userSelect: "none",
                            alignItems: "center",
                            gap: 10,
                          }}
                          title={done ? "クリックして書類詳細を表示" : "未提出のため表示できません"}
                        >
                          <div
                            style={{
                              fontWeight: 900,
                              color: "#0f172a",
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                              minWidth: 0,
                            }}
                          >
                            <span style={{ wordBreak: "break-word" }}>{title}</span>
                            {done && (
                              <span style={{ fontSize: 12, fontWeight: 900, color: "#0b1f3b" }}>
                                （詳細を見る）
                              </span>
                            )}
                          </div>

                          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                fontWeight: 900,
                                cursor: canCheck ? "pointer" : "not-allowed",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={!canCheck}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setChecklistItem("doc", docKey, e.target.checked);
                                }}
                                style={{
                                  width: 18,
                                  height: 18,
                                  cursor: canCheck ? "pointer" : "not-allowed",
                                }}
                              />
                            </label>

                            <div style={{ fontWeight: 900, color: done ? "#166534" : "#9a3412", minWidth: 46 }}>
                              {done ? "完了" : "未完了"}
                            </div>
                          </div>
                        </div>

                        {/* file info (only if uploaded) */}
                        {uploaded && (
                          <div style={{ padding: "0 10px 10px 10px" }}>
                            <div style={{ marginTop: 6, fontSize: 12, fontWeight: 800, color: "#334155" }}>
                              ファイル：
                              <a
                                href={`${BASE_URL}/staff/documents/${merchantId}/${uploaded.document_key}/download`}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  marginLeft: 6,
                                  color: "#2563eb",
                                  textDecoration: "underline",
                                  fontWeight: 900,
                                  wordBreak: "break-all",
                                }}
                                title="クリックして開く／ダウンロード"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {uploaded.file_name}
                              </a>
                            </div>

                            <div style={{ marginTop: 4, fontSize: 12, fontWeight: 700, color: "#64748b" }}>
                              提出日：{uploaded.uploaded_at}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* -------------------------
             稟議書作成 Button (bottom)
          ------------------------- */}
          <div style={{ marginTop: 16 }}>
            <button
              disabled={!allChecked}
              onClick={() => {
                if (!merchantId) return;
                navigate(`/staff/progress/${merchantId}/ringi`);
              }}
              style={{
                width: "100%",
                padding: "14px 16px",
                borderRadius: 14,
                border: "1px solid #1d4ed8",
                background: allChecked ? "#2563eb" : "#cbd5e1",
                color: allChecked ? "#fff" : "#475569",
                fontWeight: 900,
                fontSize: 16,
                cursor: allChecked ? "pointer" : "not-allowed",
              }}
              title={allChecked ? "稟議書作成へ進みます" : "チェックボックスを全て確認してください"}
            >
              稟議書作成
            </button>

            {!allChecked && (
              <div style={{ marginTop: 8, color: "#64748b", fontWeight: 800, fontSize: 12 }}>
                ※ フォーム・必須書類のチェックがすべて完了したら押せます。
              </div>
            )}
          </div>
        </>
      )}
    </StaffLayout>
  );
}
